import './EditGrepsrCredentialsModal.scss';
import { useCallback, useState } from 'react';
import { Form, Modal } from 'react-bootstrap';
import { AmazonAccountRow } from '@blisspointmedia/bpm-types/dist/AmazonSPAPI';
import { Button } from '@Components/Button';
import { Appearance, Design, Intent } from '@Components/designConstants';
import { TableRow } from '@Components/ModalEditWidget/modal-edit-constants';
import { useSetError } from '@src/redux/modals';
import { createGrepsrCredential } from './use-retail-ops-channels';

interface EditGrepsrCredentialsModalProps {
  show: boolean;
  onClose: () => void;
  onCreated: () => void;
  company: string;
  row: TableRow<AmazonAccountRow>;
}

export const EditGrepsrCredentialsModal = ({
  show,
  onClose,
  onCreated,
  company,
  row
}: EditGrepsrCredentialsModalProps) => {
  const setError = useSetError();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfa, setMfa] = useState('');
  const [saving, setSaving] = useState(false);

  const resetForm = useCallback(() => {
    setEmail('');
    setPassword('');
    setMfa('');
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const message = await createGrepsrCredential({
        company,
        asp_id: row.account_id,
        asp_account_name: row.account_name,
        email,
        password,
        mfa
      });
      resetForm();
      onCreated();
      handleClose();
      setError({ intent: Intent.SUCCESS, message });
    } catch (e) {
      setError({
        message: `Failed to create Grepsr credentials: ${e.message}`,
        reportError: e
      });
    } finally {
      setSaving(false);
    }
  }, [
    company,
    email,
    handleClose,
    mfa,
    onCreated,
    password,
    resetForm,
    row.account_id,
    row.account_name,
    setError
  ]);

  const canSave = Boolean(email) && Boolean(password) && Boolean(mfa) && !saving;

  return (
    <Modal show={show} onHide={handleClose} className='edit-grepsr-credentials-modal' centered>
      <Modal.Header closeButton>
        <div className='edit-grepsr-credentials-title-container'>
          <h2 className='edit-grepsr-credentials-title'>
            Set Up Grepsr for {row.account_name || row.account_id}
          </h2>
          <p className='edit-grepsr-credentials-subtitle'>Amazon Account ID: {row.account_id}</p>
        </div>
      </Modal.Header>
      <Modal.Body>
        <Form.Group>
          <Form.Label>ASP Email</Form.Label>
          <Form.Control
            type='email'
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder='ASP email'
          />
        </Form.Group>
        <Form.Group>
          <Form.Label>ASP Password</Form.Label>
          <Form.Control
            type='password'
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder='ASP password'
          />
        </Form.Group>
        <Form.Group>
          <Form.Label>MFA</Form.Label>
          <Form.Control
            type='text'
            value={mfa}
            onChange={e => setMfa(e.target.value)}
            placeholder='MFA secret'
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button appearance={Appearance.OUTLINED} design={Design.NEUTRAL} onClick={handleClose}>
          Cancel
        </Button>
        <Button disabled={!canSave} loading={saving} onClick={handleSave}>
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
